// src/oura/components/IssueFixDrawer.tsx
//
// The full sub-view for a Site finding, in the kit's drawer: About the issue,
// the fix steps, and the COMPLETE affected-pages table (the issue-specific
// column and the discovered timestamp) shown in full with no pagination. The
// kit supplies the dialog: focus moves in on open, Tab stays inside, Escape
// and the overlay close it, and focus goes back to the control that opened it.

import type { AeoTrackerSummaryTask } from "@service/aeo";
import { AddAsDeliverable } from "../delivery/AddAsDeliverable";
import { DELIVERY_ON } from "../delivery/flag";
import { ArrowLink, Drawer, DrawerBlock, Tag } from "@oura/ui";
import type { AuditIssue } from "../audit";
import { blockLabel } from "../tracker";
import { IssuePagesRemediation } from "./IssuePagesRemediation";
import { SEVERITY_TONE, SEVERITY_WORD } from "./site/siteIssues";

/**
 * Wider than the kit's default on purpose: this one carries a table with a
 * URL, a metric, a date, a status and an action per row, plus the generated
 * drafts underneath. At 680px every column fought for space and the URLs
 * truncated to their (identical) host. Never wider than the viewport, so on
 * a phone it is the whole screen and the table scrolls sideways inside it.
 */
const DRAWER_WIDTH = 1040;

export function IssueFixDrawer({
    issue,
    businessId,
    onRefreshData,
    trackerTask,
    onOpenTask,
    onClose,
    deliveryBusinessId,
}: {
    /** `setAside` rides along when the caller has removed the reviewer's
     *  out-of-scope pages, so the header can account for the difference between
     *  this count and the one the backend holds. */
    issue: (AuditIssue & { setAside?: number }) | null;
    /** Live business id. Present = Oura can generate and publish a fix for this
     *  issue; absent (fixture) = the static steps above are all there is. */
    businessId?: string;
    /** #3514: passed straight through to the remediation list so a status write
     *  is re-read from the server rather than living only in component state. */
    onRefreshData?: () => void | Promise<void>;
    /**
     * The plan step this finding closes (#4445), already joined by the caller on
     * the shared audit-check key.
     *
     * Absent means one of three different things: no plan generated yet, the
     * plan does not cover this check, or the summary call failed. None of them
     * is safely distinguishable here, so absent renders NOTHING rather than
     * "not in the plan", which would be a claim this component cannot support.
     */
    trackerTask?: AeoTrackerSummaryTask;
    /** Jump to the Tracker anchored on that task. Absent = render it as text. */
    onOpenTask?: (taskId: string) => void;
    onClose: () => void;
    /** The business id Delivery knows it by (a sample business too): "Add as a deliverable". */
    deliveryBusinessId?: string;
}) {
    // The caller keys this per finding, so closing unmounts the open sheet and
    // the kit's cleanup hands focus back to whatever opened it.
    if (!issue) return null;

    return (
        <Drawer
            open
            onClose={onClose}
            width={DRAWER_WIDTH}
            title={issue.label}
            sub={
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <Tag tone={SEVERITY_TONE[issue.severity]}>{SEVERITY_WORD[issue.severity]}</Tag>
                    {/* `tracker` is a fixture-only reference into the original
                        GSheet ("#7"). A live finding has nothing to put there,
                        so the clause shows only when there is something to show. */}
                    <span>
                        {issue.count} {issue.count === 1 ? "page" : "pages"}
                        {issue.setAside ? ` · ${issue.setAside} out of scope` : ""}
                        {issue.tracker ? ` · Tracker ${issue.tracker}` : ""}
                    </span>
                    {/* The plan step this finding closes (#4445): an attribute
                        of the finding, like the severity and the page count. */}
                    {trackerTask && <TrackerLine task={trackerTask} onOpenTask={onOpenTask} />}
                </span>
            }
        >
            <DrawerBlock label="About the issue">
                <p className="m-0 leading-relaxed text-gray-600">{issue.about}</p>
            </DrawerBlock>

            {DELIVERY_ON && deliveryBusinessId && <AddAsDeliverable businessId={deliveryBusinessId} checkKey={issue.key} checkLabel={issue.label} />}

            {/* Unnumbered: these are criteria the fix has to satisfy, not an
                ordered procedure. A check that sends no steps gets no block:
                the pages below and their locations are the fix then. */}
            {issue.fix.length > 0 && (
                <DrawerBlock label="How to fix">
                    <ul className="list-plain m-0 space-y-1.5 p-0">
                        {issue.fix.map((step) => (
                            <li key={step} className="flex items-start gap-2 leading-relaxed text-gray-700">
                                <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-gray-400" />
                                <span>{step}</span>
                            </li>
                        ))}
                    </ul>
                </DrawerBlock>
            )}

            {/* The steps above tell a human what to do. This does it. Pages and
                drafts are one section: a draft is about a page, so it belongs
                under that page. */}
            <DrawerBlock label="Affected pages">
                <IssuePagesRemediation issue={issue} businessId={businessId} onRefreshData={onRefreshData} />
            </DrawerBlock>
        </Drawer>
    );
}

/**
 * The tracker step this finding is the evidence for.
 *
 * Past tense once the step is done, because a finding that is still open while
 * its task is marked complete is a real state (the check reruns on the next
 * scan and has not yet verified), and calling that "closes" would tell the
 * reader the work is still ahead of them when it is behind.
 */
function TrackerLine({
    task,
    onOpenTask,
}: {
    task: AeoTrackerSummaryTask;
    onOpenTask?: (taskId: string) => void;
}) {
    const step = `${task.action} · ${blockLabel(task.week)}`;
    return (
        <span className="inline-flex min-w-0 flex-wrap items-center gap-x-1.5">
            <span aria-hidden className="text-gray-300">·</span>
            {task.status === "done" ? "Closed by" : "Closes"}
            {onOpenTask ? (
                <ArrowLink onClick={() => onOpenTask(task.id)}>{step}</ArrowLink>
            ) : (
                <span className="font-medium text-gray-900">{step}</span>
            )}
        </span>
    );
}
